import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Pagination,
  Panel,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import {
  AUDIT_REASON_MIN_LENGTH,
  PAYOUT_ACCOUNT_STATUS_WORDS,
  type PageMeta,
  type PayoutAccountReviewRow,
  type PayoutAccountStatus,
  type ReviewPayoutAccountRequest,
} from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  IDENTITY_WITHHELD,
  decisionOf,
  documentOf,
  idNameOf,
  nameWords,
} from './payout-review-names.ts';
import { datedStamp, reasonProblem, reasonReady } from './payout-state.ts';

/**
 * Player funds → Payout accounts — a mismatched holder name, decided by a second person.
 *
 * A player registers a payout account once and types the name its holder carries. The platform
 * compares that name with the one on the player's verified ID: a match is usable at once, and a
 * mismatch waits HERE for a person. Nothing can be paid to the account until one accepts it, and a
 * verdict is given once.
 *
 * THE DATA SUPPLY, in full:
 *   GET  /api/payout-accounts/review   the accounts in one standing, oldest first, in the brand scope
 *
 * THE WRITE, in full:
 *   POST /api/payout-accounts/:id/review   { verdict: ACTIVE | REFUSED, reason }, behind funds.approve
 *
 * THE TWO NAMES ARE SHOWN, NOT SCORED. The drawer puts the name on the account beside the name on
 * the ID and sets in bold the words that are in only one of them. The words are split the way the
 * platform splits them when it decides MATCHED or MISMATCH (`nameTokens` in the API's
 * name-match.ts, copied into payout-review-names.ts and held to it by a test), so what is bold is
 * what it compared; but the bold is a reading aid for the person, not a verdict, and
 * nothing on this screen says "these match". A name that is not on the ID may still be the
 * player's own — a married name, a nickname the card prints — which is why a person decides.
 *
 * THE ID IS DRAWN ONLY FOR A READER THE API SENT IT TO. The list opens with transactions.view, but
 * the name on the ID, the kind of document and its last four go only to a reader who decides
 * accounts (funds.approve) or reviews identities (kyc.review). For anybody else `identityShown` is
 * false, the list and the drawer say the ID is withheld, and the drawer names the two keys —
 * never "none on record", which would say the player has no ID.
 *
 * AN UNREAD LIST IS NOT AN EMPTY ONE. The empty state is said only when the read succeeded and
 * returned nothing; a read that failed says so, in the message that opens and in the table.
 *
 * BOTH VERDICTS ASK WHY, and both dialogs are not dismissible: a refusal is final, an acceptance
 * lets money go to an account, and a stray Escape would throw away the decision together with the
 * reason already typed. The reason is the database's own length (AUDIT_REASON_MIN_LENGTH), so the
 * screen never accepts what the platform will refuse.
 */

const PAGE_SIZE = 25;

/**
 * The four standings a person can look at, in the order the work is: what is waiting first — and
 * the ONE word each is called on this tab, by the chip that filters for it and by the tag on the
 * drawer of an account in it.
 *
 * The words are the contract's (PAYOUT_ACCOUNT_STATUS_WORDS), except for ACTIVE: the contract calls
 * it "Ready to use", which is what the PLAYER is told. A reviewer filtering for the accounts that
 * are in use is asking which ones were accepted, so the chip says that, and so does the tag.
 */
export const STANDINGS = [
  { key: 'PENDING_REVIEW', label: PAYOUT_ACCOUNT_STATUS_WORDS.PENDING_REVIEW },
  { key: 'ACTIVE', label: 'Accepted' },
  { key: 'REFUSED', label: PAYOUT_ACCOUNT_STATUS_WORDS.REFUSED },
  { key: 'RETIRED', label: PAYOUT_ACCOUNT_STATUS_WORDS.RETIRED },
] as const satisfies readonly { key: PayoutAccountStatus; label: string }[];

/** Each standing's one word (`STANDINGS`), for the drawer's tag. */
const STANDING_WORDS = Object.fromEntries(STANDINGS.map((s) => [s.key, s.label])) as Record<
  PayoutAccountStatus,
  string
>;

/** Nocturne carries state with a ramp plus a written label, never colour alone. */
const STANDING_TONE: Record<PayoutAccountStatus, TagTone> = {
  PENDING_REVIEW: 'accent',
  ACTIVE: 'neutral',
  REFUSED: 'outline',
  RETIRED: 'quiet',
};

/**
 * What an empty list means, for the standings that are not the work queue. Every one says that the
 * read succeeded: a list that failed to load has its own message and never reaches these.
 */
const REAL_ZERO = 'The read succeeded, so this is a real zero and not a list that failed to load.';
const EMPTY_TITLES: Record<Exclude<PayoutAccountStatus, 'PENDING_REVIEW'>, string> = {
  ACTIVE: 'No accepted accounts',
  REFUSED: 'Nothing has been refused',
  RETIRED: 'Nothing has been retired',
};

/* ── The two names ───────────────────────────────────────────────────────────
   DISPLAY ONLY. Which words of the two names to look at is `nameWords`, in payout-review-names.ts:
   the platform's own split of a name into the words it compares, held to the API's by a test. It
   decides nothing — the API decided MATCHED or MISMATCH when the account was registered. */

/**
 * A name as it was printed, with each word that is not in the other name set in bold.
 *
 * Compared with nothing (`against` is null) no word is bold: there is no difference to point at.
 */
function NameWords({ name, against }: { name: string; against: string | null }) {
  return (
    <>
      {nameWords(name, against).map(({ word, differs }, i) => (
        <Fragment key={`${i}-${word}`}>
          {i > 0 ? ' ' : null}
          {differs ? <strong>{word}</strong> : word}
        </Fragment>
      ))}
    </>
  );
}

/* ── The table ───────────────────────────────────────────────────────────── */

const COLUMNS: Column<PayoutAccountReviewRow>[] = [
  {
    key: 'player',
    header: 'Player',
    render: (r) => (
      <>
        <span className="tabular">{r.playerRef}</span>
        <div className="text-subtle" style={{ fontSize: '11.5px' }}>
          {r.playerName}
        </div>
      </>
    ),
  },
  { key: 'brand', header: 'Brand', render: (r) => r.brandName },
  {
    key: 'rail',
    header: 'Rail and bank',
    render: (r) => (
      <>
        {r.methodName}
        {r.bankName !== null ? (
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {r.bankName}
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'mask',
    header: 'Account (mask)',
    render: (r) => <span className="tabular">{r.mask}</span>,
  },
  { key: 'holder', header: 'Name on the account', render: (r) => r.holderName },
  {
    key: 'identity',
    header: 'Name on the ID',
    render: (r) =>
      r.identityShown ? (
        (idNameOf(r.identityFullName) ?? <span className="text-subtle">none on record</span>)
      ) : (
        <span className="text-subtle">{IDENTITY_WITHHELD}</span>
      ),
  },
  {
    key: 'registered',
    header: 'Registered',
    render: (r) => <span className="tabular">{datedStamp.format(new Date(r.createdAt))}</span>,
  },
];

/* ── One read of the tab ─────────────────────────────────────────────────── */

type AccountsPage = { items: PayoutAccountReviewRow[]; meta: PageMeta };

/**
 * ONE READ OF THE TAB: a page of the accounts in one standing, and the number waiting for review —
 * the figure the screen's header shows, read with every read of the tab whichever chip is on. The
 * waiting list counts itself; beside any other standing one row of it is read, for its total. A
 * count that could not be read is null — not reported, and never a zero — and costs the list
 * nothing: the list is what the reviewer asked for.
 */
export async function readAccounts(q: {
  standing: PayoutAccountStatus;
  page: number;
  brandId: string | null;
}): Promise<AccountsPage & { waiting: number | null }> {
  const url = (status: PayoutAccountStatus, perPage: number, page: number) => {
    const search = new URLSearchParams({ status, perPage: String(perPage), page: String(page) });
    if (q.brandId) search.set('brandId', q.brandId);
    return `/api/payout-accounts/review?${search}`;
  };
  const [list, waiting] = await Promise.all([
    api.get<AccountsPage>(url(q.standing, PAGE_SIZE, q.page)),
    q.standing === 'PENDING_REVIEW'
      ? Promise.resolve(null)
      : api.get<AccountsPage>(url('PENDING_REVIEW', 1, 1)).then(
          (d) => d.meta.total,
          () => null,
        ),
  ]);
  return { ...list, waiting: q.standing === 'PENDING_REVIEW' ? list.meta.total : waiting };
}

/* ── The list ────────────────────────────────────────────────────────────── */

/** What the tab is showing: one standing's page of accounts, and where the read of it stands. */
export interface StandingList {
  standing: PayoutAccountStatus;
  page: number;
  rows: PayoutAccountReviewRow[];
  meta: PageMeta | null;
  read: 'loading' | 'ready' | 'failed';
}

/**
 * A PRESS ON A STANDING'S CHIP, and what the list becomes.
 *
 * Another standing: its first page, and the previous standing's rows go at once — the list is
 * reading from this render on, not from the read's own start one render later, which would first
 * draw the new chip's "nothing here" over a list nobody has read. Until the new list arrives the
 * reading cue shows, never another standing's accounts under this chip.
 *
 * The chip already chosen — the chips call back for it too: back to its first page, as every list's
 * chips do, and nothing emptied. The list is read when its standing, page or brand changes, or a
 * verdict asks for it; on page 1 a press on the chosen chip changes none of them, so no read is
 * made, and an emptied list would show the reading cue over that read for good. From a later page
 * the first page is read like any page: the rows stay on screen, marked as being read again.
 */
export function chooseStanding(list: StandingList, pressed: PayoutAccountStatus): StandingList {
  if (pressed === list.standing) return list.page === 1 ? list : { ...list, page: 1 };
  return { standing: pressed, page: 1, rows: [], meta: null, read: 'loading' };
}

/**
 * The accounts in one standing: the table, what an empty list means, the count, the pages.
 *
 * WHILE A READ IS IN FLIGHT THE LIST SAYS SO. A chip switch empties it first (the tab drops the
 * previous standing's rows), so the reading cue shows rather than the old list under the new chip;
 * a page, or the list read again after a verdict, keeps the rows it has and is marked busy, with the
 * count line saying it is being read.
 */
export function AccountsList({
  standing,
  rows,
  meta,
  read,
  onOpen,
  onPage,
}: {
  standing: PayoutAccountStatus;
  rows: PayoutAccountReviewRow[];
  meta: PageMeta | null;
  read: 'loading' | 'ready' | 'failed';
  onOpen: (row: PayoutAccountReviewRow) => void;
  onPage: (page: number) => void;
}) {
  if (read === 'loading' && rows.length === 0) {
    return (
      <div className="skeleton" style={{ height: '240px', marginTop: 'var(--space-3)' }} aria-busy="true">
        &nbsp;
      </div>
    );
  }
  const rereading = read === 'loading';
  return (
    <>
      <div style={{ marginTop: 'var(--space-3)' }} aria-busy={rereading}>
        <DataTable
          caption="Registered payout accounts with player, brand, rail and bank, masked account number, the name on the account, the name on the player's verified ID (shown to reviewers only) and when each was registered"
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={onOpen}
          empty={
            read === 'failed' ? (
              <EmptyState icon="warning-circle" title="Payout accounts could not be read">
                The read failed, so this table is empty because we could not fetch it — not
                because there are no accounts to show. The reason is in the message that just
                opened.
              </EmptyState>
            ) : standing === 'PENDING_REVIEW' ? (
              <EmptyState icon="check-circle" title="None waiting">
                Every registered account&rsquo;s name matched its player&rsquo;s verified ID, or
                has been decided or retired.
              </EmptyState>
            ) : (
              <EmptyState icon="stack" title={EMPTY_TITLES[standing]}>
                {REAL_ZERO}
              </EmptyState>
            )
          }
        />
      </div>

      <div className="text-subtle" style={{ fontSize: '12px', paddingTop: 'var(--space-2)' }}>
        {meta
          ? `${formatCount(rows.length)} of ${formatCount(meta.total)} payout account${
              meta.total === 1 ? '' : 's'
            } · oldest first${rereading ? ' · reading the list again…' : ''}`
          : read === 'failed'
            ? 'the read failed — there is no count to report'
            : 'counting payout accounts…'}
      </div>

      {meta && (
        <Pagination page={meta.page} perPage={meta.perPage} total={meta.total} onPage={onPage} />
      )}
    </>
  );
}

/* ── The account in front of the reviewer ────────────────────────────────── */

/**
 * The drawer's body: the account's standing, its two names side by side with the words that are in
 * only one of them in bold, the document the ID name was read from, the account itself, and — once
 * it is decided — who decided it and why.
 */
export function AccountUnderReview({ account }: { account: PayoutAccountReviewRow }) {
  /* The ID is drawn only for a reader the API sent it to. `identityShown` false means the name and
     the document are withheld, which is not the same as the player having none on record — and the
     flag is read, not the nulls it comes with. */
  const withheld = !account.identityShown;
  const idName = withheld ? null : idNameOf(account.identityFullName);
  const document = withheld ? null : documentOf(account);
  const notShown = <span className="text-subtle">{IDENTITY_WITHHELD}</span>;
  return (
    <div className="stack">
      <div className="row">
        <Tag tone={STANDING_TONE[account.status]}>{STANDING_WORDS[account.status]}</Tag>
      </div>

      <Panel title="The two names">
        <dl className="stack" style={{ margin: 0 }}>
          <div className="def-row">
            <dt>Name on the account</dt>
            <dd>
              <NameWords name={account.holderName} against={idName} />
            </dd>
          </div>
          <div className="def-row">
            <dt>Name on the ID</dt>
            <dd>
              {withheld ? (
                notShown
              ) : idName !== null ? (
                <NameWords name={idName} against={account.holderName} />
              ) : (
                <span className="text-subtle">none on record</span>
              )}
            </dd>
          </div>
          <div className="def-row">
            <dt>Document</dt>
            <dd className="tabular">
              {withheld
                ? notShown
                : (document ?? <span className="text-subtle">none on record</span>)}
            </dd>
          </div>
        </dl>
        <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
          {withheld ? (
            <>
              The name on the ID and its document are shown only to a reader who holds{' '}
              <span className="signin-perm">funds.approve</span> or{' '}
              <span className="signin-perm">kyc.review</span>. Your role holds neither, so they are
              not shown here.
            </>
          ) : idName !== null ? (
            'Words in bold appear in only one of the two names. Capitals, accents, punctuation and endings such as Jr. are not compared.'
          ) : (
            'No name from a verified ID is on record for this player, so there is nothing to compare this name with.'
          )}
        </div>
      </Panel>

      <Panel title="The account">
        <dl className="stack" style={{ margin: 0 }}>
          <div className="def-row">
            <dt>Rail</dt>
            <dd>{account.methodName}</dd>
          </div>
          <div className="def-row">
            <dt>Bank</dt>
            <dd>{account.bankName ?? <span className="text-subtle">no bank on this rail</span>}</dd>
          </div>
          <div className="def-row">
            <dt>Account</dt>
            <dd className="tabular">{account.mask}</dd>
          </div>
          <div className="def-row">
            <dt>Registered</dt>
            <dd className="tabular">{datedStamp.format(new Date(account.createdAt))}</dd>
          </div>
        </dl>
      </Panel>

      {account.status !== 'PENDING_REVIEW' ? (
        <Panel title="The decision">
          <dl className="stack" style={{ margin: 0 }}>
            <div className="def-row">
              <dt>Decided</dt>
              <dd>{decisionOf(account)}</dd>
            </div>
            {account.status === 'REFUSED' && account.refusedReason !== null ? (
              <div className="def-row">
                <dt>Reason given</dt>
                <dd>{account.refusedReason}</dd>
              </div>
            ) : null}
          </dl>
        </Panel>
      ) : null}
    </div>
  );
}

/* ── The reason both verdicts carry ──────────────────────────────────────── */

function ReasonField({
  hint,
  value,
  error,
  onChange,
}: {
  hint: string;
  value: string;
  error: string | undefined;
  onChange: (next: string) => void;
}) {
  return (
    <Field label="Reason" hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          minLength={AUDIT_REASON_MIN_LENGTH}
          maxLength={500}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

/* ── What accepting means for the player ─────────────────────────────────── */

/**
 * WHEN THE PLAYER CAN WITHDRAW TO AN ACCEPTED ACCOUNT, said in the dialog before the verdict and in
 * the message after it. From now on only when a withdrawal to the account can be requested now: the
 * row's `railOffered`, which the API sets by what the player's withdraw screen and the request route
 * read — the payment method offered to the account's brand and not disabled on Payments, and not the
 * StarPay rail while no payout provider is configured. Any other account is accepted all the same,
 * and both sentences say when it can be used rather than "from now on" — as the e-mail the player is
 * sent does.
 */
export function acceptWords(account: Pick<PayoutAccountReviewRow, 'railOffered'>): {
  notice: string;
  done: string;
} {
  return account.railOffered
    ? {
        notice: 'The player can withdraw to this account from now on.',
        done: 'Accepted — the player can use it.',
      }
    : {
        notice:
          'The player can withdraw to this account once its payment method is available — no withdrawal to it can be requested yet.',
        done: 'Accepted — withdrawals to it can be requested once its payment method is available.',
      };
}

/* ── The tab ─────────────────────────────────────────────────────────────── */

type Verdict = ReviewPayoutAccountRequest['verdict'];

export function PayoutAccountsReview({
  canDecide,
  role,
  onCountChanged,
}: {
  canDecide: boolean;
  role: string;
  /** Told how many accounts are waiting for review, each time the waiting list is read. */
  onCountChanged: (waiting: number) => void;
}) {
  const { brandId } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [standing, setStanding] = useState<PayoutAccountStatus>('PENDING_REVIEW');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PayoutAccountReviewRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  /** "Still loading", "the read failed" and "it came back empty" are three different facts. */
  const [read, setRead] = useState<'loading' | 'ready' | 'failed'>('loading');
  /** Bumped after a verdict so the list re-reads. */
  const [reads, setReads] = useState(0);

  /* The parent's callback is read at the moment it is called, never a dependency of the read:
     a parent that passes a fresh function each render would otherwise re-fetch on every render. */
  const countChanged = useRef(onCountChanged);
  useEffect(() => {
    countChanged.current = onCountChanged;
  });

  /* A brand change shows a different — usually shorter — list; page 4 of the last one would be an
     empty page of this one. Adjusted during render so it is fetched once, for page 1. */
  const [pagedBrand, setPagedBrand] = useState(brandId);
  if (pagedBrand !== brandId) {
    setPagedBrand(brandId);
    setPage(1);
  }

  useEffect(() => {
    let cancelled = false;
    setRead('loading');

    readAccounts({ standing, page, brandId })
      .then((d) => {
        if (cancelled) return;
        /* The number waiting is the header's count, read with every read of the tab whichever
           standing and page this is — a colleague's verdict, or a new mismatch, reaches the header
           the next time anything here is read. */
        if (d.waiting !== null) countChanged.current(d.waiting);
        /* A page the list has shrunk away from (a colleague decided its last rows) is not "none
           waiting": go back to the first page rather than say so. */
        if (d.items.length === 0 && d.meta.total > 0 && page > 1) {
          setPage(1);
          return;
        }
        setRows(d.items);
        setMeta(d.meta);
        setRead('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        /* Drop the previous list rather than leave it under the new filter, and remember WHY the
           table is empty: the table must say "could not be read", not "none". */
        setRows([]);
        setMeta(null);
        setRead('failed');
        showError({
          title: 'Could not load the payout accounts',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The payout accounts could not be fetched. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [standing, page, brandId, reads, showError]);

  /* ── The drawer, and the verdict dialogs ────────────────────────────────── */

  const [open, setOpen] = useState<PayoutAccountReviewRow | null>(null);
  /* The dialog keeps its OWN copy of the row, so what is being decided is never read back from the
     drawer's state: the drawer is closed in the same breath as the dialog on every path that closes
     either, and a half-typed reason must never be left pointing at a row that has gone. */
  const [deciding, setDeciding] = useState<{ row: PayoutAccountReviewRow; verdict: Verdict } | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  /* The funds screen's one reason rule (payout-state.ts): the database's own floor, said once the
     reviewer has started typing and never before. */
  const tooShort = reasonProblem(reason);
  const ready = reasonReady(reason);

  const begin = (verdict: Verdict) => {
    if (open === null) return;
    setDeciding({ row: open, verdict });
    setReason('');
    setReasonError(undefined);
  };
  const closeDecision = () => {
    setDeciding(null);
    setReason('');
    setReasonError(undefined);
  };

  const decide = async () => {
    if (deciding === null || !ready || busy) return;
    const { row, verdict } = deciding;
    setBusy(true);
    try {
      const body: ReviewPayoutAccountRequest = { verdict, reason: reason.trim() };
      const decided = await api.post<PayoutAccountReviewRow>(
        `/api/payout-accounts/${encodeURIComponent(row.id)}/review`,
        body,
      );
      /* What the player can do now is read from the verdict's own row, as the API left it. */
      toast(verdict === 'ACTIVE' ? acceptWords(decided).done : 'Refused — the player is told.');
      closeDecision();
      setOpen(null);
      /* The decided account has left the waiting list: read it again from its first page, which
         also reports the new count. */
      setPage(1);
      setReads((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['reason']) {
        setReasonError(error.fields['reason']);
        return;
      }
      /* Read again either way: a verdict refused because a colleague gave one first has changed
         the list, and what is on screen is no longer true. */
      setReads((n) => n + 1);
      if (error instanceof ApiRequestError && (error.status === 404 || error.status === 409)) {
        closeDecision();
        setOpen(null);
      }
      showError({
        title: verdict === 'ACTIVE' ? `Could not accept ${row.ref}` : `Could not refuse ${row.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The decision may not have been recorded. The list has been read again — check it before trying again.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <>
      <Panel>
        <div className="row">
          <Chips
            label="Filter payout accounts by standing"
            active={standing}
            onChange={(key) => {
              const next = chooseStanding({ standing, page, rows, meta, read }, key);
              setStanding(next.standing);
              setPage(next.page);
              setRows(next.rows);
              setMeta(next.meta);
              setRead(next.read);
            }}
            options={STANDINGS}
          />
        </div>

        <AccountsList
          standing={standing}
          rows={rows}
          meta={meta}
          read={read}
          onOpen={setOpen}
          onPage={setPage}
        />
      </Panel>

      {/* ── The account in front of the reviewer ───────────────────────────────
          Outside the Panel on purpose: a drawer is fixed to the viewport only while no
          ancestor is a containing block for fixed positioning, and a panel's backdrop blur is
          one. */}
      <Drawer
        open={open !== null}
        /* Escape reaches the drawer even with a decision dialog on top of it, and the dialog itself
           ignores Escape: left alone, one stray key would close the drawer under a decision still
           being made. It closes when the decision is over. */
        onClose={() => {
          if (deciding === null) setOpen(null);
        }}
        title={open?.ref ?? ''}
        subtitle={open ? `${open.playerName} · ${open.playerRef} · ${open.brandName}` : undefined}
        actions={
          open !== null && open.status === 'PENDING_REVIEW' ? (
            canDecide ? (
              <>
                <Button onClick={() => begin('REFUSED')}>Refuse</Button>
                <Button variant="primary" onClick={() => begin('ACTIVE')}>
                  Accept
                </Button>
              </>
            ) : (
              <span className="text-subtle" style={{ fontSize: '11.5px' }}>
                Deciding needs <span className="signin-perm">funds.approve</span> — {role} does not
                hold it.
              </span>
            )
          ) : undefined
        }
      >
        {open && <AccountUnderReview account={open} />}
      </Drawer>

      {/* ── Accepting ──────────────────────────────────────────────────────────
          Not dismissible, and warn rather than danger: accepting moves no money by itself — it lets
          the player choose this account for a withdrawal, from now on or once its payment method is
          available (acceptWords), and the player can still retire it. Refusing, below, is final. */}
      <Dialog
        open={deciding?.verdict === 'ACTIVE'}
        onClose={closeDecision}
        title={deciding ? `Accept ${deciding.row.ref}?` : 'Accept payout account'}
        dismissible={false}
        actions={
          <>
            <Button onClick={closeDecision} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={!ready || busy}
              onClick={() => void decide()}
            >
              Accept the account
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              {deciding ? acceptWords(deciding.row).notice : null} You are stating that the name on
              the account is this player&rsquo;s, having compared the two names and, where it helps,
              the document.
            </div>
          </div>
          <ReasonField
            hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters — what you compared and why it is the same person.`}
            value={reason}
            error={reasonError ?? tooShort}
            onChange={(next) => {
              setReason(next);
              setReasonError(undefined);
            }}
          />
        </div>
      </Dialog>

      {/* ── Refusing ───────────────────────────────────────────────────────────
          Not dismissible, and danger: a refusal stands. The account stays on the player's list as
          the record of it, and cannot be retired to be typed in again. */}
      <Dialog
        open={deciding?.verdict === 'REFUSED'}
        onClose={closeDecision}
        title={deciding ? `Refuse ${deciding.row.ref}?` : 'Refuse payout account'}
        dismissible={false}
        actions={
          <>
            <Button onClick={closeDecision} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={!ready || busy}
              onClick={() => void decide()}
            >
              Refuse the account
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              Nothing can be paid to this account. The player is emailed that it was not accepted
              and told to register one in their own name.
            </div>
          </div>
          <ReasonField
            hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters — what you compared and why it is not the player’s own account.`}
            value={reason}
            error={reasonError ?? tooShort}
            onChange={(next) => {
              setReason(next);
              setReasonError(undefined);
            }}
          />
        </div>
      </Dialog>
    </>
  );
}
